import { lazy, Suspense, Fragment } from 'react';
import { Switch, Route } from 'react-router-dom';
import LoadingScreen from 'components/LoadingScreen';
import GuestGuard from 'components/GuestGuard';
import WikiLayout from 'layouts/WikiLayout';
import AuthGuard from 'components/AuthGuard';
import CustomLayout from 'layouts/CustomLayout';

const routesConfig: any = [
  {
    exact: true,
    guard: GuestGuard,
    path: '/users/sign_in',
    component: lazy(() => import('views/auth/userLogin'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/users/password/new',
    component: lazy(() => import('views/auth/forgetPassword'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/admin/sign_in',
    component: lazy(() => import('views/auth/adminLogin'))
  },
  {
    exact: true,
    guard: AuthGuard,
    layout: CustomLayout,
    path: '/companies/rebuyer_configuration',
    component: lazy(() => import('views/manageRebuyers')),
    permissions: ['manage_rebuyers']
  },
  {
    exact: true,
    guard: AuthGuard,
    layout: CustomLayout,
    path: '/profile',
    component: lazy(() => import('views/profile'))
  },
  {
    exact: true,
    path: '/users/password/force_edit',
    component: lazy(() => import('views/auth/forceEditPassword'))
  },
  {
    exact: true,
    guard: AuthGuard,
    layout: CustomLayout,
    path: '/company_clients',
    component: lazy(() => import('views/companyClient'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/users/password/edit',
    component: lazy(() => import('views/auth/editPassword'))
  },
  {
    exact: false,
    path: '/transactions2',
    layout: CustomLayout,
    guard: AuthGuard,
    component: lazy(() => import('views/negotiation/trays')),
    permissions: ['can_see_transaction_view']
  },
  {
    exact: true,
    path: '/vehicles/new',
    layout: CustomLayout,
    guard: AuthGuard,
    component: lazy(() => import('views/negotiation/create')),
    permissions: ['negotiation_creation', 'analyst_user']
  },
  {
    exact: false,
    path: '/fines',
    layout: CustomLayout,
    guard: AuthGuard,
    component: lazy(() => import('views/fines')),
    permissions: ['can_see_fines_section', 'analyst_user']
  },
  {
    exact: false,
    path: '/reports',
    layout: CustomLayout,
    guard: AuthGuard,
    component: lazy(() => import('views/reports')),
    permissions: []
  },
  {
    exact: false,
    path: '/unauthorized',
    guard: AuthGuard,
    layout: CustomLayout,
    component: lazy(() => import('views/pages/NotAuthorizedView'))
  },
  {
    exact: false,
    path: '/search',
    layout: CustomLayout,
    guard: AuthGuard,
    component: lazy(() => import('views/AdvancedSearch')),
    permissions: ['can_see_search_view', 'analyst_user']
  },
  {
    exact: true,
    guard: AuthGuard,
    layout: CustomLayout,
    path: '/companies/manage_users',
    component: lazy(() => import('views/ManageUsers')),
    permissions: ['show_manage_sellers']
  },
  {
    exact: true,
    guard: AuthGuard,
    layout: CustomLayout,
    path: '/companies/manage_branches',
    component: lazy(() => import('views/ManageBranches')),
    permissions: ['show_manage_branches']
  },
  {
    exact: true,
    guard: AuthGuard,
    layout: CustomLayout,
    path: '/companies/select_evaluator_priorities',
    component: lazy(() => import('views/EvaluatorPriorities')),
    permissions: ['assign_attending_evaluators']
  },
  {
    exact: true,
    path: '/wiki',
    layout: WikiLayout,
    component: lazy(() => import('views/wiki'))
  },
  {
    exact: true,
    guard: AuthGuard,
    layout: CustomLayout,
    path: '/bonus-settings',
    component: lazy(() => import('views/bonusSettings')),
    permissions: ['can_create_bonus']
  },
  {
    exact: true,
    guard: AuthGuard,
    layout: CustomLayout,
    path: '/monthly_goals',
    component: lazy(() => import('views/monthlyGoals')),
    permissions: ['show_monthly_goals', 'analyst_user']
  },
  {
    exact: true,
    path: '/reporteria',
    layout: CustomLayout,
    guard: AuthGuard,
    component: lazy(() => import('views/bi'))
  },
  {
    exact: true,
    guard: AuthGuard,
    layout: CustomLayout,
    path: '/promotions',
    component: lazy(() => import('views/promotions'))
  }
];

const renderRoutes = (routes: any) =>
  routes ? (
    <Suspense fallback={<LoadingScreen />}>
      <Switch>
        {routes.map((route: any, i: any) => {
          const Guard = route.guard || Fragment;
          const Layout = route.layout || Fragment;
          const Component = route.component;
          const permissions = route.permissions || [];

          return (
            <Route
              key={i}
              path={route.path}
              exact={route.exact}
              render={props => (
                <Guard path={route.path} permissions={permissions}>
                  <Layout>{route.routes ? renderRoutes(route.routes) : <Component {...props} />}</Layout>
                </Guard>
              )}
            />
          );
        })}
      </Switch>
    </Suspense>
  ) : null;

function Routes() {
  return renderRoutes(routesConfig);
}

export default Routes;
